import React, { useState, useMemo, useRef, useEffect } from "react";

const FACILITIES = [
  {
    id: 1,
    name: "Andhra Advanced Manufacturing Centre",
    location: "Visakhapatnam",
    district: "Visakhapatnam District",
    facilityType: "APIC Centre",
    technology: ["5-Axis CNC", "EDM", "Metrology"],
    machineType: "CNC Machines",
    process: "Machining",
    industry: "Aerospace",
    materials: "Metal",
    certifications: "ISO 9001",
    availability: "Available",
    verified: true,
    machines: 58,
    rating: 4.9,
    gradient: "radial-gradient(circle at 40% 55%, rgba(255,190,110,0.9) 0%, rgba(247,148,29,0.35) 18%, rgba(0,0,0,0) 42%), linear-gradient(120deg, #0b1330 0%, #1b2340 60%, #2a3358 100%)",
  },
  {
    id: 2,
    name: "AP Innovation Labs",
    location: "Tirupati",
    district: "Tirupati District",
    facilityType: "R&D Centre",
    technology: ["3D Printing", "Composites", "Testing"],
    machineType: "3D Printers",
    process: "Additive Manufacturing",
    industry: "Healthcare",
    materials: "Polymer",
    certifications: "ISO 13485",
    availability: "Available",
    verified: true,
    machines: 34,
    rating: 4.7,
    gradient: "radial-gradient(circle at 55% 45%, rgba(210,140,255,0.85) 0%, rgba(150,80,230,0.35) 20%, rgba(0,0,0,0) 45%), linear-gradient(135deg, #150b30 0%, #241542 60%, #34205c 100%)",
  },
  {
    id: 3,
    name: "SRM Prototyping Facility",
    location: "Amaravati",
    district: "Guntur District",
    facilityType: "University",
    technology: ["Sheet Metal", "Fabrication", "Assembly"],
    machineType: "Sheet Metal Machines",
    process: "Fabrication",
    industry: "Automotive",
    materials: "Metal",
    certifications: "None",
    availability: "Limited",
    verified: false,
    machines: 22,
    rating: 4.6,
    gradient: "linear-gradient(135deg, #1c2331 0%, #2a3446 55%, #3a4a63 100%)",
  },
  {
    id: 4,
    name: "Vizag MSME Tooling Unit",
    location: "Visakhapatnam",
    district: "Visakhapatnam District",
    facilityType: "MSME",
    technology: ["Injection Molding", "Tooling", "CNC"],
    machineType: "Molding Machines",
    process: "Molding",
    industry: "Consumer Goods",
    materials: "Plastic",
    certifications: "ISO 9001",
    availability: "Available",
    verified: true,
    machines: 40,
    rating: 4.5,
    gradient: "radial-gradient(circle at 45% 50%, rgba(120,200,255,0.8) 0%, rgba(60,140,220,0.3) 20%, rgba(0,0,0,0) 45%), linear-gradient(135deg, #0b1c30 0%, #142c48 60%, #1d3c60 100%)",
  },
  {
    id: 5,
    name: "Kakinada Testing & Certification Lab",
    location: "Kakinada",
    district: "East Godavari District",
    facilityType: "Testing Lab",
    technology: ["NDT", "Material Testing", "Calibration"],
    machineType: "Testing Equipment",
    process: "Testing",
    industry: "Oil & Gas",
    materials: "Metal",
    certifications: "NABL",
    availability: "Available",
    verified: true,
    machines: 18,
    rating: 4.8,
    gradient: "radial-gradient(circle at 50% 50%, rgba(120,255,200,0.75) 0%, rgba(40,180,140,0.3) 20%, rgba(0,0,0,0) 45%), linear-gradient(135deg, #0b2420 0%, #123830 60%, #1a4c40 100%)",
  },
  {
    id: 6,
    name: "Vijayawada Tech Innovation Hub",
    location: "Vijayawada",
    district: "Krishna District",
    facilityType: "Technology Centre",
    technology: ["IoT", "Robotics", "PCB Assembly"],
    machineType: "Robotic Arms",
    process: "Assembly",
    industry: "Electronics",
    materials: "Electronics",
    certifications: "ISO 9001",
    availability: "Limited",
    verified: false,
    machines: 27,
    rating: 4.4,
    gradient: "radial-gradient(circle at 50% 45%, rgba(255,210,120,0.8) 0%, rgba(230,150,40,0.3) 20%, rgba(0,0,0,0) 45%), linear-gradient(135deg, #1e1608 0%, #2e2410 60%, #3e3418 100%)",
  },
];

const FILTER_FIELDS = [
  { key: "location", label: "Location" },
  { key: "district", label: "District" },
  { key: "facilityType", label: "Facility Type" },
  { key: "technology", label: "Technology" },
  { key: "machineType", label: "Machine Type" },
  { key: "process", label: "Process" },
  { key: "industry", label: "Industry" },
  { key: "materials", label: "Materials" },
  { key: "certifications", label: "Certifications" },
  { key: "availability", label: "Availability" },
  { key: "verified", label: "Verified" },
];

function getOptions(key) {
  if (key === "verified") return ["Verified", "Not Verified"];
  const values = new Set();
  FACILITIES.forEach((f) => {
    const v = f[key];
    if (Array.isArray(v)) v.forEach((item) => values.add(item));
    else values.add(v);
  });
  return Array.from(values).sort();
}

function matchesFilters(facility, activeFilters) {
  return FILTER_FIELDS.every(({ key }) => {
    const selected = activeFilters[key];
    if (!selected) return true;
    if (key === "verified") {
      return facility.verified === (selected === "Verified");
    }
    const value = facility[key];
    if (Array.isArray(value)) return value.includes(selected);
    return value === selected;
  });
}

function matchesSearch(facility, term) {
  if (!term.trim()) return true;
  const haystack = [
    facility.name,
    facility.location,
    facility.district,
    facility.facilityType,
    facility.industry,
    facility.materials,
    facility.certifications,
    ...facility.technology,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(term.trim().toLowerCase());
}

function LocationPin() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7290" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="#f7941d" stroke="#f7941d" strokeWidth="1">
      <path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.7 7-6.3-3.9-6.3 3.9 1.7-7L2 9.2l7.1-.6L12 2z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0b1330" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v6c0 5-3 8-7 9-4-1-7-4-7-9V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function FilterPill({ field, activeValue, isOpen, onToggle, onSelect }) {
  const options = getOptions(field.key);
  return (
    <div className="ef-pill-wrap">
      <button
        type="button"
        className={`ef-pill ${activeValue ? "ef-pill--active" : ""}`}
        onClick={() => onToggle(field.key)}
      >
        {activeValue || field.label}
        {activeValue && (
          <span
            className="ef-pill-clear"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(field.key, null);
            }}
          >
            ×
          </span>
        )}
      </button>
      {isOpen && (
        <div className="ef-dropdown">
          {options.map((opt) => (
            <button
              type="button"
              key={opt}
              className={`ef-dropdown-item ${activeValue === opt ? "ef-dropdown-item--active" : ""}`}
              onClick={() => onSelect(field.key, opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ExploreFacilitiesSection() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleDropdown = (key) => {
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  const handleSelect = (key, value) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
    setOpenDropdown(null);
  };

  const clearAll = () => {
    setActiveFilters({});
    setSearchTerm("");
  };

  const filteredFacilities = useMemo(() => {
    return FACILITIES.filter(
      (f) => matchesFilters(f, activeFilters) && matchesSearch(f, searchTerm)
    );
  }, [activeFilters, searchTerm]);

  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  return (
    <section className="ef-section">
      <style>{`
        .ef-section {
          width: 100%;
          background: linear-gradient(180deg, #f7f9fd 0%, #eef1f8 100%);
          font-family: 'Inter', 'Segoe UI', sans-serif;
          box-sizing: border-box;
          padding: 72px 0;
        }
        .ef-wrap {
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 32px;
          box-sizing: border-box;
        }
        .ef-header { text-align: center; }
        .ef-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 9px 18px;
          border-radius: 999px;
          background: #fdecd3;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: #0b1330;
        }
        .ef-eyebrow-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: #f7941d; flex-shrink: 0;
        }
        .ef-heading {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 800;
          font-size: clamp(1.9rem, 4.5vw, 3.2rem);
          line-height: 1.15;
          letter-spacing: -0.02em;
          color: #0b1330;
          margin: 22px 0 0;
        }
        .ef-accent { color: #f7941d; }
        .ef-subheading {
          margin-top: 18px;
          font-size: clamp(0.95rem, 2vw, 1.1rem);
          line-height: 1.7;
          color: #5a6280;
          max-width: 720px;
          margin-left: auto;
          margin-right: auto;
        }

        .ef-search-row {
          margin-top: 36px;
          max-width: 780px;
          margin-left: auto;
          margin-right: auto;
          display: flex;
          align-items: center;
          gap: 10px;
          background: #ffffff;
          border-radius: 16px;
          padding: 8px 8px 8px 20px;
          box-shadow: 0 20px 45px -30px rgba(11,19,48,0.25);
          box-sizing: border-box;
        }
        .ef-search-icon { flex-shrink: 0; display: flex; }
        .ef-search-input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 1rem;
          color: #0b1330;
          background: transparent;
          min-width: 0;
          font-family: 'Inter', 'Segoe UI', sans-serif;
        }
        .ef-search-input::placeholder { color: #9aa1b8; }
        .ef-search-btn {
          background: #0b1330;
          color: #ffffff;
          border: none;
          border-radius: 11px;
          padding: 13px 26px;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .ef-filters-row {
          margin-top: 24px;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 10px;
          position: relative;
        }
        .ef-pill-wrap { position: relative; }
        .ef-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 16px;
          border-radius: 999px;
          border: 1px solid #dfe3ee;
          background: #ffffff;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 0.85rem;
          font-weight: 700;
          color: #0b1330;
          cursor: pointer;
          white-space: nowrap;
        }
        .ef-pill--active {
          background: #0b1330;
          border-color: #0b1330;
          color: #ffffff;
        }
        .ef-pill-clear {
          font-size: 1rem;
          line-height: 1;
          margin-left: 2px;
        }
        .ef-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          background: #ffffff;
          border: 1px solid #e7eaf3;
          border-radius: 14px;
          box-shadow: 0 20px 45px -20px rgba(11,19,48,0.3);
          padding: 8px;
          min-width: 180px;
          max-height: 240px;
          overflow-y: auto;
          z-index: 20;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .ef-dropdown-item {
          text-align: left;
          background: none;
          border: none;
          padding: 9px 12px;
          border-radius: 8px;
          font-size: 0.88rem;
          color: #23283a;
          cursor: pointer;
        }
        .ef-dropdown-item:hover { background: #f4f6fb; }
        .ef-dropdown-item--active {
          background: #fdecd3;
          font-weight: 700;
        }

        .ef-clear-row {
          margin-top: 16px;
          text-align: center;
          min-height: 20px;
        }
        .ef-clear-btn {
          background: none;
          border: none;
          color: #b3261e;
          font-size: 0.85rem;
          font-weight: 700;
          cursor: pointer;
          text-decoration: underline;
        }

        .ef-results-count {
          margin-top: 28px;
          text-align: center;
          font-size: 0.9rem;
          color: #6b7290;
        }

        .ef-grid {
          margin-top: 20px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px;
        }
        .ef-card {
          background: #ffffff;
          border: 1px solid #e7eaf3;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 45px -30px rgba(11,19,48,0.2);
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
        }
        .ef-card-image {
          width: 100%;
          aspect-ratio: 16 / 10;
          position: relative;
        }
        .ef-verified-badge {
          position: absolute;
          top: 14px;
          right: 14px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: linear-gradient(135deg, #f7941d, #ef7f0e);
          color: #0b1330;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 0.8rem;
          padding: 7px 14px;
          border-radius: 999px;
        }
        .ef-card-body {
          padding: 20px 22px 22px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }
        .ef-card-meta-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 0.85rem;
          color: #6b7290;
          gap: 8px;
          flex-wrap: wrap;
        }
        .ef-card-location {
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }
        .ef-card-title {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 1.15rem;
          color: #0b1330;
          margin: 10px 0 12px;
        }
        .ef-card-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 16px;
        }
        .ef-card-tag {
          background: #f4f6fb;
          border: 1px solid #e7eaf3;
          border-radius: 999px;
          padding: 6px 12px;
          font-size: 0.78rem;
          font-weight: 600;
          color: #3c4157;
        }
        .ef-card-footer {
          margin-top: auto;
          padding-top: 16px;
          border-top: 1px solid #eef0f6;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          flex-wrap: wrap;
        }
        .ef-machines {
          font-size: 0.88rem;
          color: #6b7290;
        }
        .ef-rating {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-weight: 700;
          font-size: 0.9rem;
          color: #0b1330;
        }
        .ef-explore-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 0.9rem;
          color: #0b1330;
          background: none;
          border: none;
          cursor: pointer;
        }
        .ef-empty-state {
          margin-top: 20px;
          text-align: center;
          padding: 48px 20px;
          color: #6b7290;
          font-size: 1rem;
          background: #ffffff;
          border: 1px dashed #dfe3ee;
          border-radius: 18px;
        }

        @media (max-width: 640px) {
          .ef-section { padding: 48px 0; }
          .ef-wrap { padding: 0 20px; }
          .ef-search-row {
            flex-wrap: wrap;
            padding: 14px;
            border-radius: 18px;
          }
          .ef-search-input { width: 100%; order: 1; padding: 4px 0; }
          .ef-search-icon { order: 0; }
          .ef-search-btn { order: 2; width: 100%; text-align: center; justify-content: center; }
          .ef-grid { grid-template-columns: 1fr; }
          .ef-pill { font-size: 0.8rem; padding: 8px 13px; }
        }
      `}</style>

      <div className="ef-wrap" ref={wrapRef}>
        <div className="ef-header">
          <span className="ef-eyebrow">
            <span className="ef-eyebrow-dot" />
            EXPLORE FACILITIES
          </span>

          <h2 className="ef-heading">
            Explore manufacturing &amp;
            <br />
            <span className="ef-accent">innovation facilities.</span>
          </h2>

          <p className="ef-subheading">
            Search across every registered facility by technology, machine,
            industry or location.
          </p>
        </div>

        <div className="ef-search-row">
          <span className="ef-search-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9aa1b8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            className="ef-search-input"
            type="text"
            placeholder="Search facilities, technologies, machines or capabilities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="button" className="ef-search-btn" onClick={() => {}}>
            Search
          </button>
        </div>

        <div className="ef-filters-row">
          {FILTER_FIELDS.map((field) => (
            <FilterPill
              key={field.key}
              field={field}
              activeValue={activeFilters[field.key]}
              isOpen={openDropdown === field.key}
              onToggle={handleToggleDropdown}
              onSelect={handleSelect}
            />
          ))}
        </div>

        <div className="ef-clear-row">
          {(activeFilterCount > 0 || searchTerm) && (
            <button type="button" className="ef-clear-btn" onClick={clearAll}>
              Clear all filters
            </button>
          )}
        </div>

        <div className="ef-results-count">
          Showing {filteredFacilities.length} of {FACILITIES.length} facilities
        </div>

        {filteredFacilities.length === 0 ? (
          <div className="ef-empty-state">
            No facilities match your filters. Try clearing a filter or search term.
          </div>
        ) : (
          <div className="ef-grid">
            {filteredFacilities.map((f) => (
              <div className="ef-card" key={f.id}>
                <div className="ef-card-image" style={{ background: f.gradient }}>
                  {f.verified && (
                    <span className="ef-verified-badge">
                      <ShieldIcon />
                      Verified
                    </span>
                  )}
                </div>
                <div className="ef-card-body">
                  <div className="ef-card-meta-row">
                    <span className="ef-card-location">
                      <LocationPin />
                      {f.location}
                    </span>
                    <span>{f.facilityType}</span>
                  </div>
                  <h3 className="ef-card-title">{f.name}</h3>
                  <div className="ef-card-tags">
                    {f.technology.map((t, i) => (
                      <span className="ef-card-tag" key={i}>
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="ef-card-footer">
                    <span className="ef-machines">{f.machines} machines</span>
                    <span className="ef-rating">
                      <StarIcon />
                      {f.rating}
                    </span>
                    <button type="button" className="ef-explore-link">
                      Explore →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}